/***
 *
 *   TABLE
 *   Please view the full documentation: https://docs.usegravity.app/ui/table
 *
 *   PROPS
 *   data: array of objects for body and header (optional)
 *   search: bool to show the search field
 *   sort: allow the table columns to be sorted
 *   loading: bool to toggle loading spinner
 *   badge - object containing column name and color to add badges to column
 *   show - array of columns (object key names) to show (shows all if not provided)
 *   hide - array of columns (object key names) to hide
 *   actions: object with edit/delete keys set to callback functions (optional)
 *
 **********/

import { Fragment, useState, useEffect } from 'react';
import { Search } from 'components/lib';
import { Header } from './header';
import { Body } from './body';
import Style from './table.tailwind.js';
import { UMLoader } from '@urbanmetrix/um-kit';

interface TableHeaderColumn {
  name: string;
  title?: string;
  sort?: boolean;
  hover?: string;
}

interface TableRow {
  id?: string | number;
  [key: string]: unknown;
}

interface TableDataWithBody {
  header?: TableHeaderColumn[];
  body?: TableRow[];
}

type TableData = TableRow[] & TableDataWithBody;

interface TableActionsConfig {
  edit?: unknown;
  view?: unknown;
  delete?: unknown;
  email?: unknown;
  custom?: unknown;
}

interface TableBadgeConfig {
  col: string;
  color: string;
  condition?: { value: unknown; color: string }[];
}

interface TableProps {
  data?: TableData;
  search?: boolean;
  sort?: boolean;
  loading?: boolean;
  badge?: TableBadgeConfig;
  show?: string[];
  hide?: string[];
  actions?: TableActionsConfig;
  throttle?: number;
  naked?: boolean;
  customStyle?: boolean;
}

export function Table({
  customSpacing = false, // new prop to enable custom spacing and vertical lines
  ...props
}: TableProps & { customSpacing?: boolean }) {
  // state
  const [header, setHeader] = useState<TableHeaderColumn[] | null>(null);
  const [body, setBody] = useState<TableRow[] | null>(null);
  const [filter, setFilter] = useState<TableRow[] | false>(false);

  useEffect(() => {
    if (props.data) {
      // create the headers
      let header = props.data.header || [];

      if (!header.length) {
        for (let key in props.data[0]) {
          header.push({
            name: key,
            title: key.replace('_', ' '),
            sort: key === 'actions' ? false : true,
          });
        }
      }
      // This code was updated by Huseyin
      // setBody(props.data);
      if (props.data.body) {
        setBody(props.data.body);
      } else {
        setBody(props.data);
      }
      setHeader(header);
    }
  }, [props.data]);

  // loading
  if (props.loading) {
    return (
      <div className={Style.loading}>
        <UMLoader />
      </div>
    );
  }

  // no data
  if (!header && !body) return false;

  function sort(column: string, direction: string) {
    const rows: TableRow[] = filter && filter.length ? [...filter] : [...(body || [])];

    rows.sort(function (rowA, rowB) {
      let a: unknown = rowA;
      let b: unknown = rowB;
      const cellA = (rowA as Record<string, { badge?: unknown; label?: unknown }>)[column];
      const cellB = (rowB as Record<string, { badge?: unknown; label?: unknown }>)[column];
      if (cellA && cellB) {
        a = cellA.badge ? cellA.label : cellA;

        b = cellB.badge ? cellB.label : cellB;

        const valA = a as number | string;
        const valB = b as number | string;
        if (direction === 'desc') {
          if (valA > valB) return -1;
          if (valA < valB) return 1;
          else return 0;
        } else {
          if (valA < valB) return -1;
          if (valA > valB) return 1;
          else return 0;
        }
      } else {
        return 0;
      }
    });

    filter ? setFilter(rows) : setBody(rows);
  }

  function search(term: string) {
    // search each cell in each row &
    // update state to show only filtered rows
    let rowsToShow: TableRow[] = [];

    (body || []).forEach((row) => {
      for (let cell in row) {
        if (row[cell]?.toString().toLowerCase().includes(term.toLowerCase())) {
          if (!rowsToShow.includes(row)) rowsToShow.push(row);
        }
      }
    });

    setFilter(rowsToShow);
  }

  function editRowCallback(res: unknown, row: TableRow) {
    let state = [...(body || [])];
    let stateRow = state[state.findIndex((x) => x.id === row.id)];
    const resMap = res as Record<string, { value: unknown }>;
    Object.keys(resMap).map((key) => (stateRow[key] = resMap[key].value));
    setBody(state);
  }

  function deleteRowCallback(_res: unknown, row: TableRow) {
    let state = [...(body || [])];
    state.splice(
      state.findIndex((x) => x.id === row.id),
      1
    );
    setBody(state);
  }

  const tableStyle = `${Style.table} ${customSpacing ? 'customTable' : ''}`;

  return (
    <Fragment>
      {props.search && <Search className={Style.search} callback={search} throttle={props.throttle} />}

      <table className={!props.naked ? tableStyle : undefined}>
        {header && <Header data={header} callback={sort} show={props.show} hide={props.hide} actions={props.actions} />}
        {body && (
          <Body
            data={filter ? filter : body}
            show={props.show}
            hide={props.hide}
            badge={props.badge}
            customStyle={props.customStyle}
            actions={{
              edit: props.actions?.edit,
              view: props.actions?.view,
              delete: props.actions?.delete,
              email: props.actions?.email,
              custom: props.actions?.custom,
            }}
            callback={{
              edit: editRowCallback,
              delete: deleteRowCallback,
            }}
          />
        )}
      </table>
    </Fragment>
  );
}
